import { Story, LiveUpdate, Video, Author } from './types';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';

export interface HomeData {
  hero: Story[];
  latest: Story[];
  breaking: Story[];
  livePost: Story | null;
  mostRead: Story[];
  sections: {
    [key: string]: Story[];
  };
  liveUpdates: LiveUpdate[];
  videos: Video[];
}

export async function getHomeData(): Promise<HomeData> {
  const res = await fetch(`${API_URL}/home`, {
    next: { revalidate: 60 }, // Revalidate every minute
  });

  if (!res.ok) {
    throw new Error('Failed to fetch home data');
  }

  const data = await res.json();
  
  // Map backend naming to frontend naming (camelCase)
  const mapPost = (post: any): Story => {
    const isClassName = post.image_bg?.startsWith('bg-');
    const storageUrl = (process.env.NEXT_PUBLIC_STORAGE_URL || 'http://127.0.0.1:8000/storage').replace(/\/$/, '');
    const imageUrl = isClassName 
      ? post.image_bg 
      : post.image_bg 
        ? `${storageUrl}/${post.image_bg.replace(/^\//, '')}`
        : 'bg-gray-200';

    return {
      ...post,
      imageBg: imageUrl,
      publishedAt: post.published_at,
      updatedAt: post.updated_at,
      readingTime: post.reading_time,
      author: post.author?.name || 'Veritas News',
      authorSlug: post.author?.slug || 'veritas-news',
      authorDetails: post.author ? {
        ...post.author,
        avatarBg: post.author.avatar_bg,
        articleCount: post.author.article_count,
        yearsAtVeritas: post.author.years_at_org,
      } : undefined
    };
  };

  return {
    hero: data.hero.map(mapPost),
    latest: data.latest.map(mapPost),
    breaking: data.breaking.map(mapPost),
    livePost: data.livePost ? mapPost(data.livePost) : null,
    mostRead: data.mostRead.map(mapPost),
    sections: Object.keys(data.sections).reduce((acc: any, key) => {
      acc[key] = data.sections[key].map(mapPost);
      return acc;
    }, {}),
    liveUpdates: data.liveUpdates,
    videos: data.videos.map((v: any) => ({
        ...v,
        id: String(v.id),
        imageBg: v.image_bg,
        videoUrl: v.video_url
    })),
  };
}

export async function getPostBySlug(slug: string): Promise<Story | null> {
  const res = await fetch(`${API_URL}/posts/${slug}`, {
    next: { revalidate: 60 },
  });

  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error('Failed to fetch post');
  }

  const { data } = await res.json();
  
  // Reuse mapPost logic or similar
  return {
    ...data,
    imageBg: data.image_bg,
    publishedAt: data.published_at,
    updatedAt: data.updated_at,
    readingTime: data.reading_time,
    author: data.author?.name || 'Veritas News',
    authorSlug: data.author?.slug || 'veritas-news',
    authorDetails: data.author ? {
      ...data.author,
      avatarBg: data.author.avatar_bg,
      articleCount: data.author.article_count,
      yearsAtVeritas: data.author.years_at_org,
    } : undefined
  };
}

export async function getPostsBySection(section: string, subsection?: string, limit = 10): Promise<Story[]> {
  let url = `${API_URL}/posts?section=${section}&limit=${limit}`;
  if (subsection && subsection !== 'All') {
    url += `&subsection=${encodeURIComponent(subsection)}`;
  }
  
  const res = await fetch(url, {
    next: { revalidate: 60 },
  });

  if (!res.ok) {
    throw new Error('Failed to fetch section posts');
  }

  const { data } = await res.json();
  
  return data.map((post: any) => ({
    ...post,
    imageBg: post.image_bg,
    publishedAt: post.published_at,
    updatedAt: post.updated_at,
    readingTime: post.reading_time,
    author: post.author?.name || 'Veritas News',
    authorSlug: post.author?.slug || 'veritas-news',
  }));
}
export async function getVideos(category?: string, limit = 10): Promise<Video[]> {
  let url = `${API_URL}/videos?limit=${limit}`;
  if (category) {
    url += `&category=${encodeURIComponent(category)}`;
  }

  const res = await fetch(url, {
    next: { revalidate: 60 },
  });

  if (!res.ok) {
    throw new Error('Failed to fetch videos');
  }

  const { data } = await res.json();
  
  return data.map((v: any) => ({
    ...v,
    id: String(v.id),
    imageBg: v.image_bg,
    videoUrl: v.video_url
  }));
}
export async function getVideoById(id: string): Promise<Video | null> {
  const res = await fetch(`${API_URL}/videos/${id}`, {
    next: { revalidate: 60 },
  });

  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error('Failed to fetch video');
  }

  const { data } = await res.json();
  
  return {
    ...data,
    id: String(data.id),
    imageBg: data.image_bg,
    videoUrl: data.video_url
  };
}
export async function getPostsByCategory(categorySlug: string, limit = 10): Promise<Story[]> {
  const res = await fetch(`${API_URL}/posts?category=${categorySlug}&limit=${limit}`, {
    next: { revalidate: 60 },
  });

  if (!res.ok) {
    throw new Error('Failed to fetch category posts');
  }

  const { data } = await res.json();
  
  return data.map((post: any) => ({
    ...post,
    imageBg: post.image_bg,
    publishedAt: post.published_at,
    updatedAt: post.updated_at,
    readingTime: post.reading_time,
    author: post.author?.name || 'Veritas News',
    authorSlug: post.author?.slug || 'veritas-news',
  }));
}

export async function getAuthors(): Promise<Author[]> {
  const res = await fetch(`${API_URL}/authors`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) {
    throw new Error('Failed to fetch authors');
  }

  const { data } = await res.json();
  
  return data.map((author: any) => ({
    ...author,
    avatarBg: author.avatar_bg,
    articleCount: author.article_count,
    yearsAtVeritas: author.years_at_org,
  }));
}
